// vg-binding: P7.D-44
// vg-binding: P7.D-45
// vg-binding: P7.D-134
// vg-binding: P7.D-154
//
// apps/vendor/src/features/payouts/WithdrawModal.tsx
//
// Phase 7 Wave 11 Task 39 — withdraw modal.
// UAT #2185 — two-step flow: amount+bank → email OTP confirm → submit.

import { useEffect, useMemo, useState } from 'react';

import { Button, Input } from '@printway/ui';
import { formatCents } from '@printway/shared/currency';

import { useTranslation } from '../../lib/i18n/index.js';
import {
  useBankAccounts,
  useInitiateWithdraw,
  useSendWithdrawOtp,
  type BankRow,
  type WalletApiError,
} from '../wallet/api.js';

interface Props {
  open: boolean;
  available_cents: number;
  onCancel: () => void;
  onSuccess: () => void;
}

export function WithdrawModal({ open, available_cents, onCancel, onSuccess }: Props): JSX.Element | null {
  const { t } = useTranslation('wallet');
  const [amountStr, setAmountStr] = useState('');
  const [bankId, setBankId] = useState('');
  const [errorMsg, setErrorMsg] = useState<string | null>(null);
  const [flagWarning, setFlagWarning] = useState<string | null>(null);
  // UAT #2185 — OTP step state.
  const [step, setStep] = useState<'form' | 'otp'>('form');
  const [otpCode, setOtpCode] = useState('');
  const banksQ = useBankAccounts();
  const initiateM = useInitiateWithdraw();
  const sendOtpM = useSendWithdrawOtp();

  useEffect(() => {
    if (!open) {
      setAmountStr('');
      setBankId('');
      setErrorMsg(null);
      setFlagWarning(null);
      setStep('form');
      setOtpCode('');
    }
  }, [open]);

  const verifiedBanks = useMemo<BankRow[]>(
    () => (banksQ.data?.rows ?? []).filter((b) => b.is_verified),
    [banksQ.data],
  );

  // Default-pick first verified bank when modal opens.
  useEffect(() => {
    if (open && !bankId && verifiedBanks.length > 0) {
      setBankId(verifiedBanks[0]!.bank_id);
    }
  }, [open, bankId, verifiedBanks]);

  if (!open) return null;

  const amountCents = Math.round(Number.parseFloat(amountStr || '0') * 100);
  const amountValid = Number.isFinite(amountCents) && amountCents >= 5000;
  const canSubmit =
    amountValid &&
    amountCents <= available_cents &&
    bankId.length === 24 &&
    !initiateM.isPending;

  // UAT #2185 — step 1: gửi OTP email rồi chuyển sang bước nhập mã.
  async function handleRequestOtp(): Promise<void> {
    setErrorMsg(null);
    try {
      await sendOtpM.mutateAsync();
      setStep('otp');
    } catch (err) {
      const e = err as WalletApiError;
      setErrorMsg(e.userMessage ?? e.message);
    }
  }

  // UAT #2185 — step 2: submit kèm mã OTP.
  async function handleSubmit(): Promise<void> {
    setErrorMsg(null);
    setFlagWarning(null);
    try {
      const r = await initiateM.mutateAsync({
        amount_cents: amountCents,
        payment_method_id: bankId,
        otp_code: otpCode,
      });
      if (r.is_flagged_suspicious) {
        // Request IS created (just held for manual admin review). Show the
        // generic notice once — the internal heuristic codes (flag_reasons)
        // are an admin-only signal, never surfaced to the vendor.
        setFlagWarning(t('withdrawModal.flaggedNotice'));
        return;
      }
      onSuccess();
    } catch (err) {
      const e = err as WalletApiError;
      setErrorMsg(e.userMessage ?? e.message);
    }
  }

  return (
    <div
      role="dialog"
      aria-modal="true"
      data-testid="withdraw-modal"
      /* dark-ok: modal scrim, translucent black reads correctly in both modes */
      className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 p-4"
    >
      <div className="w-full max-w-md rounded-xl border border-pw-border bg-pw-bg p-6 shadow-xl">
        <h2 className="text-lg font-semibold text-pw-text">{t('withdrawModal.title')}</h2>
        <p className="mt-1 text-xs text-pw-text-muted">
          {t('withdrawModal.summary', { available: formatCents(available_cents) })}
        </p>

        <div className="mt-4 flex flex-col gap-3">
          <label className="flex flex-col gap-1">
            <span className="text-sm font-medium text-pw-text">{t('withdrawModal.amountLabel')}</span>
            <Input
              type="number"
              min={50}
              step={0.01}
              placeholder={t('withdrawModal.amountPlaceholder')}
              data-testid="withdraw-amount-input"
              value={amountStr}
              onChange={(e) => setAmountStr(e.target.value)}
              className="border-pw-border bg-pw-bg text-pw-text focus:border-pw-primary focus:outline-none focus:ring-1 focus:ring-pw-primary"
            />
            {amountStr.length > 0 && amountCents < 5000 ? (
              <p className="text-xs text-yellow-700">
                {t('withdrawModal.amountTooLow')}
              </p>
            ) : null}
            {amountStr.length > 0 && amountCents > available_cents ? (
              <p className="text-xs text-red-700">
                {t('withdrawModal.amountExceedsAvailable', { available: formatCents(available_cents) })}
              </p>
            ) : null}
          </label>

          <label className="flex flex-col gap-1">
            <span className="text-sm font-medium text-pw-text">{t('withdrawModal.bankLabel')}</span>
            <select
              data-testid="withdraw-bank-select"
              value={bankId}
              onChange={(e) => setBankId(e.target.value)}
              className="h-10 rounded-md border border-pw-border bg-pw-bg px-3 text-sm text-pw-text focus:border-pw-primary focus:outline-none focus:ring-1 focus:ring-pw-primary"
            >
              <option value="">{t('withdrawModal.bankSelectPlaceholder')}</option>
              {verifiedBanks.map((b) => (
                <option key={b.bank_id} value={b.bank_id}>
                  {b.method.toUpperCase()} · {b.account_holder} · {b.account_number.slice(-4)}
                  {b.is_default ? t('withdrawModal.defaultSuffix') : ''}
                </option>
              ))}
            </select>
            {verifiedBanks.length === 0 ? (
              <p className="text-xs text-yellow-700">
                {t('withdrawModal.noVerifiedBank')}
              </p>
            ) : null}
          </label>
        </div>

        {errorMsg ? (
          <p className="mt-3 rounded-md border border-red-200 bg-red-50 px-3 py-2 text-sm text-red-700">
            {errorMsg}
          </p>
        ) : null}
        {flagWarning ? (
          <p className="mt-3 rounded-md border border-yellow-200 bg-yellow-50 px-3 py-2 text-sm text-yellow-700">
            ⚠ {flagWarning}
          </p>
        ) : null}

        {step === 'otp' ? (
          <div className="mt-4 flex flex-col gap-1 rounded-md border border-pw-border bg-pw-surface p-3">
            <span className="text-sm font-medium text-pw-text">
              {t('withdrawModal.otpLabel')}
            </span>
            <p className="text-xs text-pw-text-muted">{t('withdrawModal.otpHint')}</p>
            <Input
              type="text"
              inputMode="numeric"
              maxLength={6}
              placeholder="000000"
              data-testid="withdraw-otp-input"
              value={otpCode}
              onChange={(e) => setOtpCode(e.target.value.replace(/\D/g, '').slice(0, 6))}
              className="mt-1 border-pw-border bg-pw-bg text-center font-mono text-lg tracking-[0.4em] text-pw-text"
            />
            <button
              type="button"
              data-testid="withdraw-otp-resend"
              onClick={() => void handleRequestOtp()}
              disabled={sendOtpM.isPending}
              className="mt-1 self-start text-xs text-pw-primary-strong underline disabled:opacity-50"
            >
              {sendOtpM.isPending
                ? t('withdrawModal.otpResendPending')
                : t('withdrawModal.otpResend')}
            </button>
          </div>
        ) : null}

        <p className="mt-3 text-xs text-pw-text-muted">
          {t('withdrawModal.submitDisclaimer')}
        </p>

        <div className="mt-4 flex justify-end gap-2">
          {flagWarning ? (
            // Request landed (just flagged for admin review). Don't offer a
            // second submit — collapse the action bar to a single Đóng so
            // the vendor doesn't accidentally create a duplicate withdrawal.
            <Button
              type="button"
              data-testid="withdraw-modal-close"
              onClick={onSuccess}
              className="rounded-md bg-pw-primary px-4 py-2 text-sm font-semibold text-white hover:bg-pw-primary-strong"
            >
              {t('withdrawModal.closeButton')}
            </Button>
          ) : (
            <>
              <Button
                type="button"
                variant="outline"
                data-testid="withdraw-modal-cancel"
                onClick={onCancel}
                className="rounded-md border border-pw-border bg-pw-surface px-4 py-2 text-sm font-medium text-pw-text hover:bg-pw-bg"
              >
                {t('withdrawModal.cancelButton')}
              </Button>
              {step === 'form' ? (
                <Button
                  type="button"
                  data-testid="withdraw-modal-request-otp"
                  disabled={!canSubmit || sendOtpM.isPending}
                  onClick={() => void handleRequestOtp()}
                  className="rounded-md bg-pw-primary px-4 py-2 text-sm font-semibold text-white hover:bg-pw-primary-strong disabled:cursor-not-allowed disabled:opacity-50"
                >
                  {sendOtpM.isPending
                    ? t('withdrawModal.otpSendPending')
                    : t('withdrawModal.otpSendButton')}
                </Button>
              ) : (
                <Button
                  type="button"
                  data-testid="withdraw-modal-submit"
                  disabled={!canSubmit || otpCode.length !== 6}
                  onClick={() => void handleSubmit()}
                  className="rounded-md bg-pw-primary px-4 py-2 text-sm font-semibold text-white hover:bg-pw-primary-strong disabled:cursor-not-allowed disabled:opacity-50"
                >
                  {initiateM.isPending ? t('withdrawModal.submitPending') : t('withdrawModal.submitIdle')}
                </Button>
              )}
            </>
          )}
        </div>
      </div>
    </div>
  );
}

export default WithdrawModal;
